/**
 * Activity metadata structure for title enhancement.
 */
interface ActivityTitleMetadata {
  playerName?: string;
  player?: {
    name?: string;
    knownName?: string;
    firstName?: string;
    lastName?: string;
  };
  knownName?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  createdBy?: string;
  creatorName?: string;
  user?: {
    name?: string;
  };
  userFirstName?: string;
  userLastName?: string;
}

/**
 * Resolves the creator/user name from available metadata sources.
 * @param creatorName - Explicitly provided creator name (highest priority)
 * @param metadata - Activity metadata containing user information
 * @returns Trimmed display name for the activity creator, defaults to "Someone"
 */
const resolveCreatorName = (
  creatorName: string | undefined,
  metadata: ActivityTitleMetadata | undefined
): string => {
  const rawUserName =
    creatorName ||
    metadata?.userName ||
    metadata?.createdBy ||
    metadata?.creatorName ||
    metadata?.user?.name ||
    (metadata?.userFirstName && metadata?.userLastName
      ? `${metadata.userFirstName} ${metadata.userLastName}`
      : null) ||
    "Someone";

  return rawUserName?.trim() || "Someone";
};

/**
 * Resolves the player name from metadata and body fallback sources.
 * @remarks
 * Priority order: metadata.playerName > metadata.player.name > metadata.name
 * > notificationBody > body. Player names are sourced from the backend-canonical
 * `name` field (SE-5469) — the metadata blob carries `player.name`/`name`
 * directly, so no client-side assembly of firstName/knownName/lastName is done.
 * @param metadata - Activity metadata containing player information
 * @param notificationBody - Notification-specific body text
 * @param body - Activity body text
 * @returns Trimmed player name or null if none found
 */
const resolvePlayerName = (
  metadata: ActivityTitleMetadata | undefined,
  notificationBody: string | null | undefined,
  body: string | undefined
): string | null => {
  const rawPlayerName =
    metadata?.playerName ||
    metadata?.player?.name ||
    metadata?.name ||
    notificationBody ||
    body;

  return rawPlayerName?.trim() || null;
};

/**
 * Title templates keyed by activity type, using %player% and %user% placeholders.
 * @remarks
 * Each template uses bold markdown syntax (**text**) to make names stand out in the Activity Feed.
 */
const ACTIVITY_TITLE_TEMPLATES: Record<string, string> = {
  NOTE_CREATED: "Note created for **%player%** by **%user%**",
  SCOUT_REPORT_CREATED: "Report submitted for **%player%** by **%user%**",
  SCOUT_REPORT_UPDATED: "Report updated for **%player%** by **%user%**",
  SCOUT_REPORT_ARCHIVED: "Scout report archived for **%player%**",
  NOTE_SHARED: "Note shared for **%player%** by **%user%**",
  // CONTENT_SHARED deliberately has no template (SE-5194). Share activities
  // are direction-aware — the backend writes "You shared a scout report" for
  // the sharer and "<sharer> shared a report with you" for the recipient.
  // A template here would render the recipient's entry as "Note shared for
  // <player> by you" (the activity belongs to the recipient, so %user%
  // resolves to "you"), which is wrong on both counts.
  SCOUT_REPORT_SHARED: "Report shared for **%player%** by **%user%**",
  // MANUAL_PLAYER_* types use the backend's activity.title directly because it
  // contains the historical player name at the time of the event. Reconstructing
  // the title here would show the player's current name, which is wrong when the
  // player has been renamed since the activity was created.
};

/**
 * Enhances activity titles with player-specific information.
 * @remarks
 * Enriches activity titles for NOTE_CREATED and SCOUT_REPORT_CREATED activities
 * by extracting player and creator names from various metadata sources. Player
 * names come from the backend-canonical `name` field (SE-5469), which already
 * includes multi-part names like "Virgil van Dijk" in full — so no client-side
 * assembly is needed. For other activity types, returns the original title
 * unchanged.
 *
 * Player name resolution priority:
 * 0. resolvedPlayerName (pre-resolved from GraphQL cache/query via `useActivityPlayerNames`)
 * 1. metadata.playerName or metadata.player.name (explicit name fields)
 * 2. metadata.name, notificationBody, or body (fallback sources)
 * 4. notificationTitle verbatim (only when nothing else resolved; may be truncated)
 * @param activityType - The activity type code
 * @param originalTitle - The original title from the backend
 * @param notificationTitle - The notification-specific title (may contain player name)
 * @param metadata - Activity metadata containing player information
 * @param body - Activity body text that might contain player name
 * @param notificationBody - Notification-specific body text
 * @param activityUserId - The ID of the user who created the activity (kept for backwards compatibility)
 * @param currentUserId - The ID of the current user viewing the activity (kept for backwards compatibility)
 * @param creatorName - The name of the user who created the activity
 * @param resolvedPlayerName - Pre-resolved player name from GraphQL cache or query, takes highest priority
 * @returns Enhanced title with player identification and creator attribution
 */
export const enhanceActivityTitle = (
  activityType: string,
  originalTitle: string,
  notificationTitle?: string | null,
  metadata?: ActivityTitleMetadata | Record<string, unknown> | null,
  body?: string,
  notificationBody?: string | null,
  activityUserId?: string,
  currentUserId?: string,
  creatorName?: string,
  resolvedPlayerName?: string
): string => {
  const template = ACTIVITY_TITLE_TEMPLATES[activityType];
  if (!template) {
    return originalTitle;
  }

  const typedMetadata = metadata as ActivityTitleMetadata | undefined;
  const isSelfAction =
    activityUserId && currentUserId && activityUserId === currentUserId;

  // Priority 0: Pre-resolved player name from GraphQL
  const preResolvedName = resolvedPlayerName?.trim();
  if (preResolvedName) {
    const userName = isSelfAction
      ? "you"
      : resolveCreatorName(creatorName, typedMetadata);
    return template
      .replace("%player%", preResolvedName)
      .replace("%user%", userName);
  }

  // Priority 2-3: Resolve from metadata/body
  // For archive activities, body contains a sentence (e.g. "You archived a scout report on player X"),
  // not a player name, so skip body/notificationBody fallbacks to avoid garbled titles
  const skipBodyFallback = activityType === "SCOUT_REPORT_ARCHIVED";
  const playerName = resolvePlayerName(
    typedMetadata,
    skipBodyFallback ? undefined : notificationBody,
    skipBodyFallback ? undefined : body
  );
  if (playerName) {
    const userName = isSelfAction
      ? "you"
      : resolveCreatorName(creatorName, typedMetadata);
    return template.replace("%player%", playerName).replace("%user%", userName);
  }

  // Priority 4: Use notificationTitle as fallback when nothing else resolved
  if (notificationTitle && notificationTitle !== originalTitle) {
    return notificationTitle;
  }

  return originalTitle;
};
